import React, { useState } from "react";
import { View, Text, StyleSheet, TouchableOpacity, ScrollView, TextInput } from "react-native";
import { Ionicons } from "@expo/vector-icons";

// Sample font families
const FONT_FAMILIES = [
	{ id: "system", name: "System Default", value: "System" },
	// { id: "sfprodisplay", name: "SF Pro Display", value: "SF Pro Display" }, // Removed
	// { id: "sfprotext", name: "SF Pro Text", value: "SF Pro Text" }, // Removed
	{ id: "georgia", name: "Georgia", value: "Georgia" },
	{ id: "monospace", name: "Monospace", value: "monospace" },
	{ id: "arial", name: "Arial", value: "Arial" },
	{ id: "times", name: "Times New Roman", value: "Times New Roman" },
];

// Font weights
const FONT_WEIGHTS = [
	{ id: "regular", name: "Regular", value: "400" },
	{ id: "medium", name: "Medium", value: "500" },
	{ id: "semibold", name: "SemiBold", value: "600" },
	{ id: "bold", name: "Bold", value: "700" },
];

const StylerView = ({ typographyStyles, editingStyle, stylesDispatch, onOpenColorModal }) => {
	const [searchQuery, setSearchQuery] = useState("");
	const [activeTab, setActiveTab] = useState("fonts");
	const [selectedFontId, setSelectedFontId] = useState(null);

	const isEditing = !!editingStyle;

	// Handler for opening the color picker
	const handleOpenColorPicker = () => {
		if (!editingStyle) return;
		if (onOpenColorModal) {
			onOpenColorModal(editingStyle.color);
		}
	};

	// Handler for opening the font editor
	const handleEditFont = (styleId) => {
		stylesDispatch({ type: "START_EDITING", payload: { styleId } });
	};

	// Handler for saving font changes
	const handleSaveFont = () => {
		stylesDispatch({ type: "SAVE_STYLE" });
	};

	// Handler for canceling font editing
	const handleCancelEdit = () => {
		stylesDispatch({ type: "CANCEL_EDIT" });
	};

	// Handler for creating a new typography style
	const handleCreateStyle = () => {
		stylesDispatch({ type: "CREATE_STYLE" });
	};

	// Handler for input changes in the editor
	const handleEditorInputChange = (changes) => {
		if (!editingStyle) return;
		// Allow temporary string values for fontSize and lineHeight
		stylesDispatch({ type: "UPDATE_EDITING_STYLE", payload: { changes } });
	};

	// Validation/Reset handler for Font Size on Blur
	const handleFontSizeBlur = () => {
		if (!editingStyle) return;
		const textValue = editingStyle.fontSize?.toString() ?? "";
		let numericValue = parseInt(textValue, 10); // Use radix 10

		// If invalid, reset to a default, otherwise use the parsed number
		if (isNaN(numericValue) || numericValue <= 0) {
			// Also check for 0 or less
			numericValue = 16; // Default font size
		}

		stylesDispatch({ type: "UPDATE_EDITING_STYLE", payload: { changes: { fontSize: numericValue } } });
	};

	// Validation/Reset handler for Line Height on Blur
	const handleLineHeightBlur = () => {
		if (!editingStyle) return;
		const textValue = editingStyle.lineHeight?.toString() ?? "";
		let numericValue = parseFloat(textValue);

		// If invalid, reset to a default, otherwise use the parsed number
		if (isNaN(numericValue) || numericValue <= 0) {
			// Also check for 0 or less
			numericValue = 1.5; // Default line height
		}

		stylesDispatch({ type: "UPDATE_EDITING_STYLE", payload: { changes: { lineHeight: numericValue } } });
	};

	// Helper function to get font weight name from value
	const getFontWeightName = (value) => {
		const weight = FONT_WEIGHTS.find((w) => w.value === value);
		return weight ? weight.name : value;
	};

	// Filter typography styles based on search query
	const filteredStyles = typographyStyles.filter((style) => style.name.toLowerCase().includes(searchQuery.toLowerCase()));

	// Render the editor for a typography style
	const renderFontEditor = () => {
		if (!editingStyle) return null;

		return (
			<View style={styles.editorContainer}>
				<View style={styles.editorHeader}>
					<Text style={styles.editorTitle}>{editingStyle ? "Edit Typography Style" : "Create New Style"}</Text>
					<TouchableOpacity onPress={handleCancelEdit}>
						<Ionicons name="close" size={20} color="#666" />
					</TouchableOpacity>
				</View>

				<View style={styles.editorForm}>
					<View style={styles.formGroup}>
						<Text style={styles.formLabel}>Name</Text>
						<TextInput style={styles.formInput} value={editingStyle.name} onChangeText={(text) => handleEditorInputChange({ name: text })} placeholder="Style name" placeholderTextColor="#666" />
					</View>

					<View style={styles.formGroup}>
						<Text style={styles.formLabel}>Font Family</Text>
						<View style={styles.fontFamilySelector}>
							{FONT_FAMILIES.map((font) => (
								<TouchableOpacity key={font.id} style={[styles.fontFamilyOption, editingStyle.fontFamily === font.value && styles.fontFamilyOptionSelected]} onPress={() => handleEditorInputChange({ fontFamily: font.value })}>
									<Text style={[styles.fontFamilyOptionText, { fontFamily: font.value }]}>{font.name}</Text>
								</TouchableOpacity>
							))}
						</View>
					</View>

					<View style={styles.formRow}>
						<View style={[styles.formGroup, { flex: 1 }]}>
							<Text style={styles.formLabel}>Font Size (px)</Text>
							<TextInput
								style={styles.formInput}
								value={editingStyle.fontSize.toString()}
								onChangeText={(text) => handleEditorInputChange({ fontSize: text })}
								onBlur={handleFontSizeBlur}
								keyboardType="numeric"
								placeholder="16"
								placeholderTextColor="#666"
							/>
						</View>

						<View style={[styles.formGroup, { flex: 1 }]}>
							<Text style={styles.formLabel}>Line Height</Text>
							<TextInput
								style={styles.formInput}
								value={editingStyle.lineHeight.toString()}
								onChangeText={(text) => handleEditorInputChange({ lineHeight: text })}
								onBlur={handleLineHeightBlur}
								keyboardType="numeric"
								placeholder="1.5"
								placeholderTextColor="#666"
							/>
						</View>
					</View>

					<View style={styles.formGroup}>
						<Text style={styles.formLabel}>Font Weight</Text>
						<View style={styles.weightSelector}>
							{FONT_WEIGHTS.map((weight) => (
								<TouchableOpacity key={weight.id} style={[styles.weightOption, editingStyle.fontWeight === weight.value && styles.weightOptionSelected]} onPress={() => handleEditorInputChange({ fontWeight: weight.value })}>
									<Text style={styles.weightOptionText}>{weight.name}</Text>
								</TouchableOpacity>
							))}
						</View>
					</View>

					<View style={styles.formGroup}>
						<Text style={styles.formLabel}>Text Color</Text>
						<TouchableOpacity style={styles.colorSelector} onPress={handleOpenColorPicker}>
							<View style={[styles.colorPreview, { backgroundColor: editingStyle.color }]} />
							<Text style={styles.colorValue}>{editingStyle.color}</Text>
							<Ionicons name="color-palette-outline" size={18} color="#666" />
						</TouchableOpacity>
					</View>

					<View style={styles.formPreview}>
						<Text style={styles.previewLabel}>Preview</Text>
						<View style={styles.previewBox}>
							<Text
								style={{
									fontFamily: editingStyle.fontFamily,
									fontSize: editingStyle.fontSize,
									fontWeight: editingStyle.fontWeight,
									lineHeight: (editingStyle.fontSize || 16) * (editingStyle.lineHeight || 1.5),
									color: editingStyle.color,
								}}>
								{editingStyle.name}
							</Text>
						</View>
					</View>

					<View style={styles.formActions}>
						<TouchableOpacity style={styles.cancelButton} onPress={handleCancelEdit}>
							<Text style={styles.cancelButtonText}>Cancel</Text>
						</TouchableOpacity>
						<TouchableOpacity style={styles.saveButton} onPress={handleSaveFont}>
							<Text style={styles.saveButtonText}>Save Changes</Text>
						</TouchableOpacity>
					</View>
				</View>
			</View>
		);
	};

	return (
		<View style={styles.container}>
			<View style={styles.headerRow}>
				<View style={styles.searchContainerWrapper}>
					<View style={styles.searchContainer}>
						<Ionicons name="text" size={20} color="#666" style={styles.searchIcon} />
						<TextInput style={styles.searchInput} placeholder="Search typography styles..." placeholderTextColor="#666" value={searchQuery} onChangeText={setSearchQuery} />
					</View>
				</View>

				<TouchableOpacity style={styles.createButton} onPress={handleCreateStyle}>
					<Ionicons name="add-circle-outline" size={20} color="#fff" />
					<Text style={styles.createButtonText}>New Style</Text>
				</TouchableOpacity>
			</View>

			<View style={styles.filterTabs}>
				<TouchableOpacity style={[styles.filterTab, activeTab === "fonts" && styles.activeFilterTab]} onPress={() => setActiveTab("fonts")}>
					<Text style={styles.filterTabText}>Fonts</Text>
				</TouchableOpacity>
				<TouchableOpacity style={[styles.filterTab, activeTab === "colors" && styles.activeFilterTab]} onPress={() => setActiveTab("colors")}>
					<Text style={styles.filterTabText}>Colors</Text>
				</TouchableOpacity>
				<TouchableOpacity style={[styles.filterTab, activeTab === "spacing" && styles.activeFilterTab]} onPress={() => setActiveTab("spacing")}>
					<Text style={styles.filterTabText}>Spacing</Text>
				</TouchableOpacity>
			</View>

			<ScrollView style={styles.contentContainer}>
				{isEditing ? (
					renderFontEditor()
				) : (
					<>
						{activeTab === "fonts" && (
							<View style={styles.fontsContainer}>
								<View style={styles.styleSection}>
									<Text style={styles.sectionTitle}>Typography Styles</Text>

									{filteredStyles.map((style) => (
										<TouchableOpacity key={style.id} style={[styles.fontItem, selectedFontId === style.id && styles.selectedFontItem]} onPress={() => setSelectedFontId(style.id)}>
											<View style={styles.fontDetails}>
												<Text style={styles.fontName}>{style.fontFamily}</Text>
												<Text style={styles.fontProperties}>
													{getFontWeightName(style.fontWeight)}, {style.fontSize}px, {style.lineHeight} line height
												</Text>
											</View>
											<View style={styles.fontPreview}>
												<Text
													style={{
														fontSize: style.fontSize,
														fontWeight: style.fontWeight,
														color: style.color,
														fontFamily: style.fontFamily,
													}}>
													{style.name}
												</Text>
											</View>
											<View style={styles.fontItemActions}>
												<TouchableOpacity style={styles.editButton} onPress={() => handleEditFont(style.id)}>
													<Ionicons name="pencil" size={18} color="#666" />
												</TouchableOpacity>
											</View>
										</TouchableOpacity>
									))}
								</View>
							</View>
						)}

						{activeTab === "colors" && (
							<View style={styles.colorsContainer}>
								<Text style={styles.sectionTitle}>Color System</Text>

								<View style={styles.colorSection}>
									<Text style={styles.colorSectionTitle}>Brand Colors</Text>
									<View style={styles.colorRow}>
										<View style={styles.colorBox}>
											<View style={[styles.colorSample, { backgroundColor: "#82C91E" }]} />
											<Text style={styles.colorLabel}>Primary</Text>
											<Text style={styles.colorValue}>#82C91E</Text>
										</View>

										<View style={styles.colorBox}>
											<View style={[styles.colorSample, { backgroundColor: "#4C6EF5" }]} />
											<Text style={styles.colorLabel}>Secondary</Text>
											<Text style={styles.colorValue}>#4C6EF5</Text>
										</View>

										<View style={styles.colorBox}>
											<View style={[styles.colorSample, { backgroundColor: "#FF6B6B" }]} />
											<Text style={styles.colorLabel}>Accent</Text>
											<Text style={styles.colorValue}>#FF6B6B</Text>
										</View>
									</View>
								</View>

								<View style={styles.colorSection}>
									<Text style={styles.colorSectionTitle}>Text Colors</Text>
									<View style={styles.colorRow}>
										<View style={styles.colorBox}>
											<View style={[styles.colorSample, { backgroundColor: "#FFFFFF" }]} />
											<Text style={styles.colorLabel}>Primary</Text>
											<Text style={styles.colorValue}>#FFFFFF</Text>
										</View>

										<View style={styles.colorBox}>
											<View style={[styles.colorSample, { backgroundColor: "#9E9E9E" }]} />
											<Text style={styles.colorLabel}>Secondary</Text>
											<Text style={styles.colorValue}>#9E9E9E</Text>
										</View>

										<View style={styles.colorBox}>
											<View style={[styles.colorSample, { backgroundColor: "#666666" }]} />
											<Text style={styles.colorLabel}>Disabled</Text>
											<Text style={styles.colorValue}>#666666</Text>
										</View>
									</View>
								</View>
							</View>
						)}

						{activeTab === "spacing" && (
							<View style={styles.spacingContainer}>
								<Text style={styles.sectionTitle}>Spacing System</Text>

								<View style={styles.spacingItem}>
									<View style={styles.spacingVisual}>
										<View style={[styles.spacingBlock, { width: 4, height: 36 }]} />
									</View>
									<View style={styles.spacingDetails}>
										<Text style={styles.spacingName}>XS (Extra Small)</Text>
										<Text style={styles.spacingValue}>4px</Text>
									</View>
								</View>

								<View style={styles.spacingItem}>
									<View style={styles.spacingVisual}>
										<View style={[styles.spacingBlock, { width: 8, height: 36 }]} />
									</View>
									<View style={styles.spacingDetails}>
										<Text style={styles.spacingName}>S (Small)</Text>
										<Text style={styles.spacingValue}>8px</Text>
									</View>
								</View>

								<View style={styles.spacingItem}>
									<View style={styles.spacingVisual}>
										<View style={[styles.spacingBlock, { width: 16, height: 36 }]} />
									</View>
									<View style={styles.spacingDetails}>
										<Text style={styles.spacingName}>M (Medium)</Text>
										<Text style={styles.spacingValue}>16px</Text>
									</View>
								</View>

								<View style={styles.spacingItem}>
									<View style={styles.spacingVisual}>
										<View style={[styles.spacingBlock, { width: 24, height: 36 }]} />
									</View>
									<View style={styles.spacingDetails}>
										<Text style={styles.spacingName}>L (Large)</Text>
										<Text style={styles.spacingValue}>24px</Text>
									</View>
								</View>

								<View style={styles.spacingItem}>
									<View style={styles.spacingVisual}>
										<View style={[styles.spacingBlock, { width: 32, height: 36 }]} />
									</View>
									<View style={styles.spacingDetails}>
										<Text style={styles.spacingName}>XL (Extra Large)</Text>
										<Text style={styles.spacingValue}>32px</Text>
									</View>
								</View>
							</View>
						)}
					</>
				)}
			</ScrollView>
		</View>
	);
};

const styles = StyleSheet.create({
	container: {
		flex: 1,
		display: "flex",
		flexDirection: "column",
	},
	headerRow: {
		flexDirection: "row",
		alignItems: "center",
		marginBottom: 16,
		gap: 12,
	},
	searchContainerWrapper: {
		flex: 1,
	},
	searchContainer: {
		flexDirection: "row",
		alignItems: "center",
		backgroundColor: "#2A2A2A",
		borderRadius: 4,
		paddingHorizontal: 12,
	},
	searchIcon: {
		marginRight: 8,
	},
	searchInput: {
		flex: 1,
		height: 40,
		color: "#fff",
		fontSize: 14,
	},
	createButton: {
		flexDirection: "row",
		alignItems: "center",
		justifyContent: "center",
		paddingVertical: 8,
		paddingHorizontal: 12,
		backgroundColor: "#333",
		borderRadius: 4,
		gap: 6,
	},
	createButtonText: {
		color: "#fff",
		fontSize: 14,
	},
	filterTabs: {
		flexDirection: "row",
		marginBottom: 16,
		gap: 8,
	},
	filterTab: {
		paddingVertical: 6,
		paddingHorizontal: 12,
		borderRadius: 4,
		backgroundColor: "#2A2A2A",
	},
	activeFilterTab: {
		backgroundColor: "#333",
	},
	filterTabText: {
		color: "#fff",
		fontSize: 12,
	},
	contentContainer: {
		flex: 1,
	},
	fontsContainer: {
		flex: 1,
	},
	colorsContainer: {
		flex: 1,
	},
	spacingContainer: {
		flex: 1,
	},
	styleSection: {
		marginBottom: 24,
	},
	sectionTitle: {
		fontSize: 16,
		fontWeight: "600",
		color: "#fff",
		marginBottom: 12,
	},
	fontItem: {
		flexDirection: "row",
		alignItems: "center",
		padding: 12,
		backgroundColor: "#2A2A2A",
		borderRadius: 4,
		marginBottom: 8,
	},
	selectedFontItem: {
		borderWidth: 1,
		borderColor: "#82C91E",
	},
	fontPreview: {
		flex: 1,
		justifyContent: "center",
		alignItems: "flex-start",
	},
	fontPreviewText: {
		fontSize: 24,
		fontWeight: "bold",
		color: "#fff",
	},
	fontDetails: {
		width: 180,
		marginRight: 16,
	},
	fontName: {
		fontSize: 14,
		color: "#fff",
		marginBottom: 4,
	},
	fontProperties: {
		fontSize: 12,
		color: "#666",
	},
	fontItemActions: {
		flexDirection: "row",
		alignItems: "center",
		gap: 8,
	},
	colorButton: {
		width: 32,
		height: 32,
		borderRadius: 16,
		justifyContent: "center",
		alignItems: "center",
		backgroundColor: "#333",
	},
	colorDot: {
		width: 20,
		height: 20,
		borderRadius: 10,
		borderWidth: 1,
		borderColor: "rgba(255, 255, 255, 0.2)",
	},
	editButton: {
		width: 40,
		height: 40,
		justifyContent: "center",
		alignItems: "center",
		borderRadius: 4,
		backgroundColor: "#333",
	},
	fontFamilyRow: {
		flexDirection: "row",
		flexWrap: "wrap",
		gap: 8,
	},
	fontFamilyItem: {
		padding: 12,
		backgroundColor: "#2A2A2A",
		borderRadius: 4,
		minWidth: 120,
	},
	fontFamilyText: {
		color: "#fff",
		fontSize: 14,
	},
	addButton: {
		flexDirection: "row",
		alignItems: "center",
		justifyContent: "center",
		padding: 12,
		backgroundColor: "#333",
		borderRadius: 4,
		marginTop: 16,
		gap: 8,
	},
	addButtonText: {
		color: "#fff",
		fontSize: 14,
	},
	colorSection: {
		marginBottom: 24,
	},
	colorSectionTitle: {
		fontSize: 14,
		fontWeight: "500",
		color: "#9E9E9E",
		marginBottom: 8,
	},
	colorRow: {
		flexDirection: "row",
		flexWrap: "wrap",
		gap: 16,
	},
	colorBox: {
		width: 100,
	},
	colorSample: {
		height: 48,
		borderRadius: 4,
		marginBottom: 8,
		borderWidth: 1,
		borderColor: "rgba(255, 255, 255, 0.1)",
	},
	colorLabel: {
		fontSize: 12,
		color: "#fff",
		marginBottom: 2,
	},
	colorValue: {
		fontSize: 11,
		color: "#9E9E9E",
	},
	spacingItem: {
		flexDirection: "row",
		alignItems: "center",
		padding: 12,
		backgroundColor: "#2A2A2A",
		borderRadius: 4,
		marginBottom: 8,
	},
	spacingVisual: {
		width: 100,
		alignItems: "center",
	},
	spacingBlock: {
		backgroundColor: "#82C91E",
		borderRadius: 2,
	},
	spacingDetails: {
		flex: 1,
		marginLeft: 16,
	},
	spacingName: {
		fontSize: 14,
		color: "#fff",
		marginBottom: 4,
	},
	spacingValue: {
		fontSize: 12,
		color: "#9E9E9E",
	},

	// Editor styles
	editorContainer: {
		backgroundColor: "#262626",
		borderRadius: 6,
		padding: 16,
		marginBottom: 16,
	},
	editorHeader: {
		flexDirection: "row",
		justifyContent: "space-between",
		alignItems: "center",
		marginBottom: 16,
	},
	editorTitle: {
		fontSize: 16,
		fontWeight: "600",
		color: "#fff",
	},
	editorForm: {
		gap: 16,
	},
	formGroup: {
		marginBottom: 12,
	},
	formRow: {
		flexDirection: "row",
		gap: 16,
	},
	formLabel: {
		fontSize: 12,
		color: "#9E9E9E",
		marginBottom: 8,
	},
	formInput: {
		backgroundColor: "#333",
		borderRadius: 4,
		padding: 8,
		color: "#fff",
		fontSize: 14,
	},
	fontFamilySelector: {
		flexDirection: "row",
		flexWrap: "wrap",
		gap: 8,
	},
	fontFamilyOption: {
		paddingVertical: 8,
		paddingHorizontal: 12,
		borderRadius: 4,
		backgroundColor: "#333",
		marginBottom: 8,
	},
	fontFamilyOptionSelected: {
		backgroundColor: "#444",
		borderWidth: 1,
		borderColor: "#82C91E",
	},
	fontFamilyOptionText: {
		color: "#fff",
		fontSize: 14,
	},
	weightSelector: {
		flexDirection: "row",
		gap: 8,
	},
	weightOption: {
		paddingVertical: 8,
		paddingHorizontal: 12,
		borderRadius: 4,
		backgroundColor: "#333",
	},
	weightOptionSelected: {
		backgroundColor: "#444",
		borderWidth: 1,
		borderColor: "#82C91E",
	},
	weightOptionText: {
		color: "#fff",
		fontSize: 14,
	},
	colorSelector: {
		flexDirection: "row",
		alignItems: "center",
		backgroundColor: "#333",
		borderRadius: 4,
		padding: 8,
		gap: 12,
	},
	colorPreview: {
		width: 24,
		height: 24,
		borderRadius: 4,
		borderWidth: 1,
		borderColor: "rgba(255, 255, 255, 0.2)",
	},
	colorValue: {
		flex: 1,
		color: "#fff",
		fontSize: 14,
	},
	formPreview: {
		marginTop: 16,
		marginBottom: 16,
	},
	previewLabel: {
		fontSize: 12,
		color: "#9E9E9E",
		marginBottom: 8,
	},
	previewBox: {
		backgroundColor: "#1A1A1A",
		borderRadius: 4,
		padding: 16,
		minHeight: 80,
		justifyContent: "center",
	},
	formActions: {
		flexDirection: "row",
		justifyContent: "flex-end",
		gap: 12,
		marginTop: 16,
	},
	cancelButton: {
		paddingVertical: 8,
		paddingHorizontal: 16,
		borderRadius: 4,
		backgroundColor: "#333",
	},
	cancelButtonText: {
		color: "#fff",
		fontSize: 14,
	},
	saveButton: {
		paddingVertical: 8,
		paddingHorizontal: 16,
		borderRadius: 4,
		backgroundColor: "#82C91E",
	},
	saveButtonText: {
		color: "#000",
		fontSize: 14,
		fontWeight: "600",
	},
});

export default StylerView;
